import {useCallback} from 'react';

import {type FulfillmentDetailAddressInput} from '@/graphql/types';
import {geocodedAddressResultToString} from '@/utils/geocodedAddressResult';
import {geocodeStringToFulfillmentAddress} from '@/utils/geocodeStringToFulfillmentAddress';
import {type GeocodedAddress, type GeocodedAddressResult} from '@/utils/googleAutocomplete';

type Args = {
  fetchGeocodedAddress: (addressString: string) => Promise<GeocodedAddress[]>;
  fallbackAddress?: FulfillmentDetailAddressInput;
  fallbackAddressString: string;
};

// Only the fields we key resolution off of; kept structural to avoid coupling to
// StartOrderFormKeywordLocation's FormValues.
type ResolveValues = {
  addressString: string;
  googlePlace: unknown;
  geocodedAddressResult: GeocodedAddressResult | null;
  lastResolvedAddressString: string;
};

// Writes `address` and the `lastResolvedAddressString` marker (which input produced it)
// so resolveKeywordFormAddress can reuse `address` instead of re-geocoding.
type SetFieldValue = {
  (field: 'address', value: FulfillmentDetailAddressInput | undefined): void;
  (field: 'lastResolvedAddressString', value: string): void;
};

const useManualAddressResolver = ({
  fetchGeocodedAddress,
  fallbackAddress,
  fallbackAddressString,
}: Args) => {
  return useCallback(
    async (values: ResolveValues, setFieldValue: SetFieldValue) => {
      const addressString = values.addressString.trim();

      // A picked autocomplete suggestion becomes `address` via submit-on-select.
      if (values.googlePlace) return;

      // A geolocation result becomes `address` via GeolocationAddressSync — bail
      // only while the shown text still matches it. If the user typed over it,
      // fall through and resolve what they entered; guarding on truthiness alone
      // would permanently disable manual resolution if the field went stale.
      if (
        values.geocodedAddressResult &&
        geocodedAddressResultToString(values.geocodedAddressResult) === addressString
      ) {
        return;
      }

      // Empty/fallback input → write the fallback (no geocode) so `address` tracks
      // the input instead of keeping a stale resolved value.
      if (!addressString || addressString === fallbackAddressString) {
        setFieldValue('address', fallbackAddress);
        setFieldValue('lastResolvedAddressString', addressString);
        return;
      }

      // Already resolved this exact input — `address` is up to date. Dedup on the
      // shared `lastResolvedAddressString` marker (not a local ref): every writer
      // of `address`, including GeolocationAddressSync, maintains it, so it can't
      // go stale-but-matching and cause a wrong reuse in resolveKeywordFormAddress.
      if (addressString === values.lastResolvedAddressString) return;

      // If a geolocation result lands while this geocode is in flight, our write
      // below can overwrite it — bounded to suggestions, never the submit value.
      try {
        const resolved = await geocodeStringToFulfillmentAddress(addressString, {
          fetchGeocodedAddress,
          fallbackAddress,
        });
        setFieldValue('address', resolved);
        setFieldValue('lastResolvedAddressString', addressString);
      } catch {
        // Best-effort background geocode: on failure (e.g. a network error) keep
        // the previously-shown address; the marker is only set on success, so a
        // later blur retries.
      }
    },
    [fetchGeocodedAddress, fallbackAddress, fallbackAddressString],
  );
};

export default useManualAddressResolver;
